import React from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import useOffer from 'hooks/Offers/useOffer'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import LesaPurchaseSummarySkeleton from './Common/LesaPurchaseSummarySkeleton'
import { OFFER_TYPE_TOUR } from 'constants/offer'
import MarkdownRender from 'components/Luxkit/MarkdownRender'
import { HotelSummaryInclusionsV2 } from 'components/Common/OrderView/OrderItemSummaries/HotelItemSummary/HotelSummaryInclusionsV2'
import Action, { ActionsList } from '../../Chat/MessageMeta/Action'
import LesaPurchaseSummaryMetaContainer from './Common/LesaPurchaseSummaryMetaContainer'
import { filterActions } from '../../../utils/actionsUtils'
import LesaHotelItemSummary from 'supportAssistant/components/SelectPurchase/LesaPurchaseSummary/LesaHotelItemSummary'
import FinePrintModal from '../../Chat/MessageMeta/FinePrintModal'
import { ChatActions, ContentTypes, MessageMetaContent, MessageMetaPayload } from 'api/support'
import LesaHotelPurchases from './LesaHotelPurchases'
import { isDowngraded } from 'lib/order/isDowngraded'

const LesaHotelItemSummaryContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};
`

interface Props {
  order: App.Order;
  isPastOrder: boolean;
  hideSelect?: boolean;
  actions?: MessageMetaContent[];
  componentsMap: Map<string | undefined, MessageMetaContent>;
  payload?: MessageMetaPayload
  handleAction: (message: string, meta: any) => void;
}

export default function LesaHotelPurchaseSummary(props: Props) {
  const { order, isPastOrder, hideSelect } = props
  const { actions, handleAction, payload, componentsMap } = props

  const [offer] = useOffer<App.Offer>(order.items[0]?.offerId)
  let items = order.items?.filter(i => !(i.status === 'cancelled' && isDowngraded(i)))

  // In case all items are cancelled, show the latest one
  if (items.length === 0) {
    items = order.items.slice(-1)
  }

  const item = items[0]

  const orderOffer = item?.offer
  const isTour = orderOffer?.parentType === OFFER_TYPE_TOUR

  const hasInclusionsV2 = item?.inclusions?.inclusionsV2 && item?.inclusions?.inclusionsV2.length > 0

  if (!offer) {
    return <LesaPurchaseSummarySkeleton />
  }

  const orderActions = actions?.filter(action => filterActions(action, isPastOrder, isTour ? [ChatActions.LIST_INCLUSIONS, ChatActions.SHOW_FINE_PRINT] : []))

  return (
    <>
      {(!!componentsMap?.get(ContentTypes.LIST_ORDERS) || !!componentsMap?.get(ChatActions.SELECT_ORDER)) && (
        <LesaHotelPurchases
          hideSelect={hideSelect}
          order={order}
          handleAction={handleAction}
          isPastOrder={isPastOrder}
        />
      )}
      {!!componentsMap?.get(ContentTypes.FINE_PRINT_MODAL) && (
        <FinePrintModal offer={offer} />
      )}
      {!!componentsMap?.get(ChatActions.LIST_INCLUSIONS) && (
        <LesaPurchaseSummaryMetaContainer>
          {item?.inclusions?.inclusionHighlights &&
            <>
              <BodyTextBlock variant="medium" weight="bold">Package inclusions:</BodyTextBlock>
              <MarkdownRender fontSize="medium" type="compact" content={item.inclusions.inclusionHighlights} />
            </>
          }
          {item?.inclusions?.bonusInclusions &&
            <>
              <BodyTextBlock variant="medium" weight="bold">Bonus inclusions:</BodyTextBlock>
              <MarkdownRender fontSize="medium" type="compact" content={item.inclusions.bonusInclusions} />
            </>
          }
          {(!item?.inclusions?.inclusionHighlights && !item?.inclusions?.bonusInclusions && hasInclusionsV2) &&
            <HotelSummaryInclusionsV2 inclusions={item.inclusions.inclusionsV2 as App.PackageInclusionV2[]} />
          }
        </LesaPurchaseSummaryMetaContainer>
      )}
      {!!componentsMap?.get(ContentTypes.LIST_ORDER_ITEMS) && (
        <LesaHotelItemSummaryContainer>
          {items?.map((hotelItem, index) => (
            <LesaHotelItemSummary
              key={index}
              item={hotelItem}
              order={order}
              offer={offer}
              handleItemSelect={handleAction}
              roomLabel={items?.length === 1 ? 'Your Room' : `Room ${index + 1}`}
            />
          ))}
        </LesaHotelItemSummaryContainer>
      )}
      {!!componentsMap?.get(ChatActions.SELECT_ORDER_ITEM) && (
        <LesaHotelItemSummaryContainer>
          <LesaHotelItemSummary
              hideSelectButton
              item={items.find(hotelItem => hotelItem.id === payload?.itemId)}
              order={order}
              offer={offer}
              roomLabel="Selected room"
          />
        </LesaHotelItemSummaryContainer>
      )}
      {!!orderActions?.length && <ActionsList>
        {orderActions?.map((action) => (
          <Action
            key={action.action?.key}
            action={action}
            payload={payload}
            handleAction={(text, meta) => handleAction(text, { ...meta, orderType: 'hotel' })}
          />
        ))}
      </ActionsList>}
    </>
  )
}
